'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Menu } from 'lucide-react';
import { m } from 'motion/react';
import { css, cx } from 'styled-system/css';
import { siteNav } from 'styled-system/recipes';

import Connect2Wallet from '@shaojiang/components/Connect2Wallet';
import { useDesignMotion } from '@shaojiang/components/motion';
import { Button } from '@shaojiang/components/ui/button';
import { IconButton } from '@shaojiang/components/ui/icon-button';
import type { SiteCopy } from '@shaojiang/content';
import { POSES } from '@shaojiang/data/site';
import { DesignVariants } from '@shaojiang/designs/runtime/DesignOnly';
import useLocale from '@shaojiang/hooks/useLocale';
import useLocalizedHref from '@shaojiang/hooks/useLocalizedHref';

import { Copy } from './Copy';
import { DesignSwitch } from './DesignSwitch';
import { LanguageToggle } from './LanguageToggle';
import { hideMd } from './layout';
import { isActiveItem, navItems } from './nav-items';
import { SiteSidebar } from './SiteSidebar';
import { ThemeToggle } from './ThemeToggle';

/** Only below `lg`, where the section links move into the sidebar */
const onlyNarrow = css({ lg: { display: 'none!' } });
/** Language and wallet move into the sidebar on phones */
const hideSm = css({ smDown: { display: 'none!' } });
/** On the smallest phones only the avatar shows; the name stays for screen readers */
const brandName = css({ xsDown: { srOnly: true } });

interface SiteNavProps {
  copy: SiteCopy['nav'];
  tools: SiteCopy['tools'];
  email: string;
}

/**
 * Top navigation for every page: a paper bar in Storybook, a floating glass pill in Nebula, a
 * pill of links in Bento and a frosted bar in Studio (all from the `siteNav` recipe). The current page is marked by a shape that slides from link
 * to link. Below `lg` the links live in a sidebar opened from the menu button.
 */
export function SiteNav({ copy, tools, email }: SiteNavProps) {
  const classes = siteNav();
  const { pathName } = useLocale();
  const localize = useLocalizedHref();
  const motion = useDesignMotion();
  const [menuOpen, setMenuOpen] = useState(false);

  const items = navItems(copy, tools);

  const brand = (
    <Link href={localize('/')} className={classes.brand} onClick={() => setMenuOpen(false)}>
      <Image src={POSES.hero} alt='' width={88} height={88} priority className={classes.avatar} />
      <span className={brandName}>
        <DesignVariants
          as='span'
          storybook='Shaojiang'
          nebula={
            <span>
              shaojiang<span className={css({ color: 'fg.accent' })}>.io</span>
            </span>
          }
          bento='Shaojiang'
          studio='Shaojiang'
        />
      </span>
    </Link>
  );

  return (
    <header className={classes.root}>
      <div className={classes.bar}>
        <div className={classes.inner}>
          <div className={classes.start}>
            <IconButton
              variant='surface'
              size='sm'
              aria-label={copy.openMenu}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
              className={onlyNarrow}
            >
              <Menu />
            </IconButton>
            {brand}
          </div>

          <nav aria-label={copy.menu} className={classes.links}>
            {items.map((item) => {
              const active = isActiveItem(item, pathName);
              return (
                <Link
                  key={item.href}
                  href={localize(item.href)}
                  className={classes.link}
                  aria-current={active ? 'page' : undefined}
                >
                  {active && (
                    <m.span
                      layoutId='nav-indicator'
                      className={classes.indicator}
                      transition={motion.layout}
                    />
                  )}
                  <Copy text={item.label} />
                </Link>
              );
            })}
          </nav>

          <div className={classes.actions}>
            <span className={cx(hideSm, css({ display: 'contents' }))}>
              <LanguageToggle label={copy.language} title={copy.otherLanguage} />
            </span>
            <DesignSwitch labels={copy} />
            <ThemeToggle labels={copy} />
            <span className={cx(hideSm, css({ display: 'contents' }))}>
              <Connect2Wallet label={copy.wallet} />
            </span>
            <Button asChild variant='hot' size='sm' className={hideMd}>
              <a href={`mailto:${email}`}>
                <Copy text={copy.cta} />
              </a>
            </Button>
          </div>
        </div>
      </div>

      <SiteSidebar
        open={menuOpen}
        onOpenChange={setMenuOpen}
        items={items}
        path={pathName}
        brand={brand}
        copy={copy}
        email={email}
      />
    </header>
  );
}
